import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  FileText,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  ChevronRight,
  Euro,
  Building2,
  Clock,
  CheckCircle2,
  Send,
  Loader2
} from 'lucide-react';
import { formatCurrency, formatDateShort, formatInvoiceStatus, getInvoiceStatusColor } from '../lib/utils';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = ['#0F5156', '#C27868', '#69AFAF', '#E8A87C', '#4A90A4', '#D4A574', '#7FB3B3', '#B8706A'];

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, chartsRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`),
        axios.get(`${API_URL}/dashboard/charts`),
        axios.get(`${API_URL}/invoices?limit=10&sort=-created_at`)
      ]);
      setStats(statsRes.data);
      setCharts(chartsRes.data);
      setRecentInvoices((invoicesRes.data?.invoices || invoicesRes.data || []).slice(0, 8));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const monthlyData = charts?.monthly || [
    { month: 'Jan', montant: 12500 },
    { month: 'Fev', montant: 18300 },
    { month: 'Mar', montant: 15200 },
    { month: 'Avr', montant: 22100 },
    { month: 'Mai', montant: 19800 },
    { month: 'Jun', montant: 24500 }
  ];

  const categoryData = charts?.by_category || [
    { name: 'Fournitures', value: 35 },
    { name: 'Services', value: 25 },
    { name: 'Loyer', value: 20 },
    { name: 'Transport', value: 12 },
    { name: 'Autre', value: 8 }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F5156]" />
      </div>
    );
  }

  const kpiCards = [
    {
      label: 'Total factures',
      value: stats?.total_invoices || 0,
      icon: FileText,
      color: 'bg-[#0F5156]',
      trend: stats?.invoice_trend,
      link: '/invoices'
    },
    {
      label: 'En attente de validation',
      value: stats?.pending_validation || 0,
      icon: Clock,
      color: 'bg-amber-500',
      alert: (stats?.pending_validation || 0) > 0,
      link: '/validation'
    },
    {
      label: 'Montant à payer',
      value: formatCurrency(stats?.amount_to_pay || 0),
      icon: Euro,
      color: 'bg-red-500',
      isAmount: true,
      link: '/invoices'
    },
    {
      label: 'Fournisseurs actifs',
      value: stats?.active_suppliers || 0,
      icon: Building2,
      color: 'bg-blue-500',
      link: '/suppliers'
    }
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Bienvenue, {user?.prenom || 'Utilisateur'}
          </h1>
          <p className="text-slate-500 mt-1">Voici un aperçu de votre activité comptable.</p>
        </div>
        <Badge variant="outline" className="w-fit">
          <Building2 className="w-4 h-4 mr-1" />
          {new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </Badge>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.map((kpi, i) => (
          <Link key={i} to={kpi.link} className="block">
            <Card className="group cursor-pointer hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{kpi.label}</p>
                    <div className="flex items-baseline gap-2 mt-2">
                      <span className="text-3xl font-bold text-slate-900 dark:text-white">{kpi.value}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-2">
                      {kpi.alert ? (
                        <>
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                          <span className="text-xs text-amber-500">A traiter</span>
                        </>
                      ) : kpi.trend > 0 ? (
                        <>
                          <TrendingUp className="w-4 h-4 text-emerald-500" />
                          <span className="text-xs text-emerald-500">+{kpi.trend}%</span>
                        </>
                      ) : kpi.trend < 0 ? (
                        <>
                          <TrendingDown className="w-4 h-4 text-red-500" />
                          <span className="text-xs text-red-500">{kpi.trend}%</span>
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">ce mois</span>
                      )}
                    </div>
                  </div>
                  <div className={`${kpi.color} p-3 rounded-xl group-hover:scale-110 transition-transform`}>
                    <kpi.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Area Chart - Monthly Spending */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Dépenses mensuelles</CardTitle>
            <CardDescription>Évolution du montant des factures fournisseurs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyData}>
                  <defs>
                    <linearGradient id="colorMontant" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0F5156" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0F5156" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    formatter={(value) => [formatCurrency(value), 'Montant']}
                  />
                  <Area type="monotone" dataKey="montant" stroke="#0F5156" strokeWidth={2} fill="url(#colorMontant)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart - By Category */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Par catégorie</CardTitle>
            <CardDescription>Répartition des dépenses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="45%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value}%`, 'Part']} />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value) => <span className="text-xs text-slate-600">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Invoices */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Dernières factures</CardTitle>
            <CardDescription>Factures récemment ajoutées ou modifiées</CardDescription>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to="/invoices">
              Voir tout<ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {recentInvoices.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune facture pour le moment</p>
              <Button className="mt-4 bg-[#0F5156] hover:bg-[#0d464a]" asChild>
                <Link to="/invoices">Déposer une facture</Link>
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numéro</TableHead>
                  <TableHead>Fournisseur</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Montant TTC</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentInvoices.map((invoice) => (
                  <TableRow key={invoice.id} className="cursor-pointer hover:bg-slate-50">
                    <TableCell className="font-mono text-sm">{invoice.numero || '-'}</TableCell>
                    <TableCell className="font-medium">{invoice.fournisseur_nom || invoice.supplier_name || '-'}</TableCell>
                    <TableCell>{formatDateShort(invoice.date_facture || invoice.date)}</TableCell>
                    <TableCell className="font-semibold">{formatCurrency(invoice.montant_ttc || invoice.total_ttc)}</TableCell>
                    <TableCell>
                      <Badge className={getInvoiceStatusColor(invoice.statut || invoice.status)}>
                        {formatInvoiceStatus(invoice.statut || invoice.status)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
